import type { RefObject, SubmitEventHandler } from 'react';
import type { SetFormApiFn } from '../../form-hooks/form-components/FormStateHandler/FormStateHandler';
import type {
  Plan,
  WithOptionalInternalProps,
} from '../../lib/flotiq-client/types/api-response';

import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import z from 'zod';

import {
  resetFormMeta,
  setFormSubmitErrors,
  showValidationErrorToast,
} from '../../form-hooks/lib/helpers';
import { getTestProps } from '../../lib/helpers';

import { useGridNavigate } from '../../components/DataGrid/useGridFilters';
import Heading from '../../components/Heading/Heading';

import { useAppForm } from '../../form-hooks/form';

export type PlanFormSubmitFn = (
  values: WithOptionalInternalProps<Plan>,
) => Promise<
  [
    [
      WithOptionalInternalProps<Plan> | undefined,
      Record<string, string | string[]> | undefined,
    ],
    boolean,
  ]
>;
interface PlanFormProps {
  /**
   * On submit callback
   */
  onSubmit: PlanFormSubmitFn;
  /**
   * Plan to edit
   */
  plan?: WithOptionalInternalProps<Plan> | null;
  /**
   * If form is disabled
   */
  disabled?: boolean;
  /**
   * If navigates after submit represented by react ref
   */
  navigateOnSave?: RefObject<boolean>;
  /**
   * Set form state
   */
  setFormState?: SetFormApiFn;
  /**
   * Test id for page
   */
  testId?: string;
}

const getInitialValues = (plan?: WithOptionalInternalProps<Plan> | null) => {
  return {
    name: plan?.name || '',
    visibleName: plan?.visibleName || '',
    displayOrder: plan?.displayOrder || 0,
    stripePriceApiIdMonthly: plan?.stripePriceApiIdMonthly || '',
    stripePriceApiIdYearly: plan?.stripePriceApiIdYearly || '',
    support: plan?.support || '',
    price: plan?.price || 0,
    priceYearly: plan?.priceYearly || 0,
    fileQuota: plan?.fileQuota || 0,
    maxAssetSize: plan?.maxAssetSize || 0,
    ctdLimit: plan?.ctdLimit || 0,
    ctoLimit: plan?.ctoLimit || 0,
    scopedKeysLimit: plan?.scopedKeysLimit || 0,
    teamMembersLimit: plan?.teamMembersLimit || 0,
    webhooksLimit: plan?.webhooksLimit || 0,
    hostedWebhooksLimit: plan?.hostedWebhooksLimit || 0,
    apiCallsLimit: plan?.apiCallsLimit || 0,
    bandwidthLimit: plan?.bandwidthLimit || 0,
    enabled: plan?.enabled || false,
    visible: plan?.visible || false,
    defaultPlan: plan?.defaultPlan || false,
    defaultFreePlan: plan?.defaultFreePlan || false,
    scopedKeysDocs: plan?.scopedKeysDocs || false,
    userRolesEnabled: plan?.userRolesEnabled || false,
    officialPluginsLimit: plan?.officialPluginsLimit || 0,
    customPluginsLimit: plan?.customPluginsLimit || 0,
    allowedHostsForCustomPlugins:
      plan?.allowedHostsForCustomPlugins || 'localhost',
    apiKeyManagement: plan?.apiKeyManagement || false,
    iso27001: plan?.iso27001 || false,
    uptime: plan?.uptime || '',
    techSupport: plan?.techSupport || '',
    hosting: plan?.hosting || '',
    backupFrequency: plan?.backupFrequency || '',
    historyRetention: plan?.historyRetention || '',
    versioning: plan?.versioning || false,
    creditCard: plan?.creditCard || '',
    monthlyAnnual: plan?.monthlyAnnual || false,
    invoice: plan?.invoice || false,
    usageDashboard: plan?.usageDashboard || false,
    predefinedUserRoles: plan?.predefinedUserRoles || false,
  };
};

const PlanForm = ({
  plan,
  onSubmit,
  disabled = false,
  navigateOnSave,
  setFormState,
  testId = '',
}: PlanFormProps) => {
  const { t } = useTranslation();
  const { navigateGrid } = useGridNavigate('plans', '/plans');

  const zodPlanPropertySchema = useMemo(
    () =>
      z
        .number()
        .refine(
          (value) => value >= -1 && value !== Infinity,
          t('Plans.Form.PlanPropertyNumber'),
        ),
    [t],
  );

  const validationSchema = useMemo(
    () =>
      z.object({
        name: z.string().min(1, t('Form.FormErrorNotBlank')),
        visibleName: z.string(),
        displayOrder: z.number(),
        stripePriceApiIdMonthly: z.string(),
        stripePriceApiIdYearly: z.string(),
        support: z.string(),
        price: z.number().min(-1),
        priceYearly: z.number().min(-1),
        fileQuota: zodPlanPropertySchema,
        maxAssetSize: zodPlanPropertySchema,
        ctdLimit: zodPlanPropertySchema,
        ctoLimit: zodPlanPropertySchema,
        scopedKeysLimit: zodPlanPropertySchema,
        teamMembersLimit: zodPlanPropertySchema,
        webhooksLimit: zodPlanPropertySchema,
        hostedWebhooksLimit: zodPlanPropertySchema,
        apiCallsLimit: zodPlanPropertySchema,
        bandwidthLimit: zodPlanPropertySchema,
        enabled: z.boolean(),
        visible: z.boolean(),
        defaultPlan: z.boolean(),
        defaultFreePlan: z.boolean(),
        scopedKeysDocs: z.boolean(),
        userRolesEnabled: z.boolean(),
        officialPluginsLimit: zodPlanPropertySchema,
        customPluginsLimit: zodPlanPropertySchema,
        allowedHostsForCustomPlugins: z.string(),
        apiKeyManagement: z.boolean(),
        iso27001: z.boolean(),
        uptime: z.string(),
        techSupport: z.string(),
        hosting: z.string(),
        backupFrequency: z.string(),
        historyRetention: z.string(),
        versioning: z.boolean(),
        creditCard: z.string(),
        monthlyAnnual: z.boolean(),
        invoice: z.boolean(),
        usageDashboard: z.boolean(),
        predefinedUserRoles: z.boolean(),
      }),
    [t, zodPlanPropertySchema],
  );

  const form = useAppForm({
    defaultValues: getInitialValues(plan),
    onSubmit: async ({ value, formApi }) => {
      const [[, errors], hasErrors] = await onSubmit(value);

      if (!hasErrors) {
        if (navigateOnSave?.current) {
          navigateGrid();
        }
        resetFormMeta(formApi);
      } else {
        const filteredErrors = setFormSubmitErrors(formApi, errors);
        showValidationErrorToast(filteredErrors);
      }
    },
    validators: {
      onChange: validationSchema,
    },
  });

  const handleSubmit = useCallback<SubmitEventHandler>(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      form.handleSubmit();
    },
    [form],
  );

  return (
    <form.AppForm>
      <form
        id="plan-form"
        className="space-y-2 md:space-y-4 p-5 md:py-10 md:px-12"
        onSubmit={handleSubmit}
        noValidate
      >
        <div className="flex flex-col items-left gap-3 w-full">
          <form.AppField name="name">
            {(field) => (
              <field.Input
                label={t('Plans.Name')}
                disabled={disabled}
                required
                {...getTestProps(testId, 'plan-name', 'testId')}
              />
            )}
          </form.AppField>
          <form.AppField name="visibleName">
            {(field) => (
              <field.Input label={t('Plans.VisibleName')} disabled={disabled} />
            )}
          </form.AppField>
          <form.AppField name="enabled">
            {(field) => (
              <field.Switch
                label={t('Plans.Enabled')}
                helpText={t('Plans.HelpText.Enabled')}
                disabled={disabled}
              />
            )}
          </form.AppField>
          <form.AppField name="visible">
            {(field) => (
              <field.Switch
                label={t('Plans.Visible')}
                disabled={disabled}
                helpText={t('Plans.HelpText.Visible')}
              />
            )}
          </form.AppField>
          <form.AppField name="defaultPlan">
            {(field) => (
              <field.Switch
                label={t('Plans.DefaultPlan')}
                disabled={disabled}
              />
            )}
          </form.AppField>
          <form.AppField name="defaultFreePlan">
            {(field) => (
              <field.Switch
                label={t('Plans.DefaultFreePlan')}
                disabled={disabled}
              />
            )}
          </form.AppField>
          <form.AppField name="displayOrder">
            {(field) => (
              <field.Input
                type="number"
                label={t('Plans.DisplayOrder')}
                helpText={t('Plans.HelpText.DisplayOrder')}
                disabled={disabled}
              />
            )}
          </form.AppField>
          <Heading level={3} additionalClasses="dark:text-white">
            {t('Plans.PaymentDetails')}
          </Heading>
          <form.AppField name="stripePriceApiIdMonthly">
            {(field) => (
              <field.Input
                label={t('Plans.StripePriceApiIdMonthly')}
                disabled={disabled}
              />
            )}
          </form.AppField>
          <form.AppField name="stripePriceApiIdYearly">
            {(field) => (
              <field.Input
                label={t('Plans.stripePriceApiIdYearly')}
                disabled={disabled}
              />
            )}
          </form.AppField>
          <form.AppField name="price">
            {(field) => (
              <field.Input
                label={t('Plans.Price')}
                type="number"
                disabled={disabled}
                {...getTestProps(testId, 'plan-price', 'testId')}
              />
            )}
          </form.AppField>

          <form.AppField name="priceYearly">
            {(field) => (
              <field.Input
                label={t('Plans.PriceYearly')}
                type="number"
                disabled={disabled}
                {...getTestProps(testId, 'plan-price-yearly', 'testId')}
              />
            )}
          </form.AppField>

          <Heading level={3} additionalClasses="dark:text-white">
            {t('Plans.Heading.General')}
          </Heading>

          <form.AppField name="fileQuota">
            {(field) => (
              <field.Input
                label={t('Plans.Form.FileQuota')}
                type="number"
                disabled={disabled}
                {...getTestProps(testId, 'plan-file-quota', 'testId')}
              />
            )}
          </form.AppField>

          <form.AppField name="ctdLimit">
            {(field) => (
              <field.Input
                label={t('Plans.Form.ObjectTypesLimit')}
                type="number"
                disabled={disabled}
                {...getTestProps(testId, 'plan-ctd-limit', 'testId')}
              />
            )}
          </form.AppField>

          <form.AppField name="ctoLimit">
            {(field) => (
              <field.Input
                label={t('Plans.Form.ObjectsLimit')}
                type="number"
                disabled={disabled}
                {...getTestProps(testId, 'plan-cto-limit', 'testId')}
              />
            )}
          </form.AppField>

          <form.AppField name="maxAssetSize">
            {(field) => (
              <field.Input
                label={t('Plans.Form.MaxAssetSize')}
                type="number"
                disabled={disabled}
                {...getTestProps(testId, 'plan-max-asset-size', 'testId')}
              />
            )}
          </form.AppField>

          <form.AppField name="bandwidthLimit">
            {(field) => (
              <field.Input
                label={t('Plans.Form.BandwidthLimit')}
                type="number"
                disabled={disabled}
                {...getTestProps(testId, 'plan-bandwidth-limit', 'testId')}
              />
            )}
          </form.AppField>

          <Heading level={3} additionalClasses="dark:text-white">
            {t('Plans.Heading.Webhooks')}
          </Heading>

          <form.AppField name="webhooksLimit">
            {(field) => (
              <field.Input
                label={t('Plans.Form.WebhookLimit')}
                type="number"
                disabled={disabled}
                {...getTestProps(testId, 'plan-webhooks-limit', 'testId')}
              />
            )}
          </form.AppField>

          <form.AppField name="hostedWebhooksLimit">
            {(field) => (
              <field.Input
                label={t('Plans.Form.HostedWebhooksLimit')}
                type="number"
                disabled={disabled}
                {...getTestProps(
                  testId,
                  'plan-hosted-webhooks-limit',
                  'testId',
                )}
              />
            )}
          </form.AppField>

          <Heading level={3} additionalClasses="dark:text-white">
            {t('Plans.Heading.Plugins')}
          </Heading>

          <form.AppField name="officialPluginsLimit">
            {(field) => (
              <field.Input
                label={t('Plans.Form.OfficialPluginsLimit')}
                type="number"
                disabled={disabled}
                {...getTestProps(
                  testId,
                  'plan-official-plugins-limit',
                  'testId',
                )}
              />
            )}
          </form.AppField>

          <form.AppField name="customPluginsLimit">
            {(field) => (
              <field.Input
                label={t('Plans.Form.CustomPluginsLimit')}
                type="number"
                disabled={disabled}
                {...getTestProps(testId, 'plan-custom-plugins-limit', 'testId')}
              />
            )}
          </form.AppField>

          <form.AppField name="allowedHostsForCustomPlugins">
            {(field) => (
              <field.Input
                label={t('Plans.Form.AllowedHostsForCustomPlugins')}
                disabled={disabled}
                {...getTestProps(testId, 'plan-allowed-hosts-limit', 'testId')}
              />
            )}
          </form.AppField>

          <Heading level={3} additionalClasses="dark:text-white">
            {t('Plans.Heading.APIAccess')}
          </Heading>

          <form.AppField name="apiCallsLimit">
            {(field) => (
              <field.Input
                label={t('Plans.Form.ApiCallsLimit')}
                type="number"
                disabled={disabled}
                {...getTestProps(testId, 'plan-api-calls-limit', 'testId')}
              />
            )}
          </form.AppField>

          <Heading level={3} additionalClasses="dark:text-white">
            {t('Plans.Heading.SecuritySLA')}
          </Heading>

          <form.AppField name="apiKeyManagement">
            {(field) => (
              <field.Switch
                label={t('Plans.Form.ApiKeyManagement')}
                disabled={disabled}
              />
            )}
          </form.AppField>

          <form.AppField name="scopedKeysLimit">
            {(field) => (
              <field.Input
                label={t('Plans.Form.ScopedKeysLimit')}
                type="number"
                disabled={disabled}
                {...getTestProps(testId, 'plan-scoped-keys-limit', 'testId')}
              />
            )}
          </form.AppField>

          <form.AppField name="scopedKeysDocs">
            {(field) => (
              <field.Switch
                label={t('Plans.Form.ScopedKeysDocs')}
                disabled={disabled}
              />
            )}
          </form.AppField>

          <form.AppField name="iso27001">
            {(field) => (
              <field.Switch
                label={t('Plans.Form.Iso27001')}
                disabled={disabled}
              />
            )}
          </form.AppField>

          <form.AppField name="uptime">
            {(field) => (
              <field.Input label={t('Plans.Form.Uptime')} disabled={disabled} />
            )}
          </form.AppField>

          <form.AppField name="support">
            {(field) => (
              <field.Input label={t('Plans.Support')} disabled={disabled} />
            )}
          </form.AppField>

          <form.AppField name="techSupport">
            {(field) => (
              <field.Input
                label={t('Plans.Form.TechSupport')}
                disabled={disabled}
              />
            )}
          </form.AppField>

          <Heading level={3} additionalClasses="dark:text-white">
            {t('Plans.Heading.Teamwork')}
          </Heading>

          <form.AppField name="teamMembersLimit">
            {(field) => (
              <field.Input
                label={t('Plans.Form.TeamMembersLimit')}
                type="number"
                disabled={disabled}
                {...getTestProps(testId, 'plan-team-members-limit', 'testId')}
              />
            )}
          </form.AppField>

          <form.AppField name="userRolesEnabled">
            {(field) => (
              <field.Switch
                label={t('Plans.Form.UserRolesEnabled')}
                disabled={disabled}
              />
            )}
          </form.AppField>

          <form.AppField name="predefinedUserRoles">
            {(field) => (
              <field.Switch
                label={t('Plans.Form.PredefinedUserRoles')}
                disabled={disabled}
              />
            )}
          </form.AppField>

          <Heading level={3} additionalClasses="dark:text-white">
            {t('Plans.Heading.Hosting')}
          </Heading>

          <form.AppField name="hosting">
            {(field) => (
              <field.Input
                label={t('Plans.Form.Hosting')}
                disabled={disabled}
              />
            )}
          </form.AppField>

          <Heading level={3} additionalClasses="dark:text-white">
            {t('Plans.Heading.Backups')}
          </Heading>

          <form.AppField name="backupFrequency">
            {(field) => (
              <field.Input
                label={t('Plans.Form.BackupFrequency')}
                disabled={disabled}
              />
            )}
          </form.AppField>

          <form.AppField name="historyRetention">
            {(field) => (
              <field.Input
                label={t('Plans.Form.HistoryRetention')}
                disabled={disabled}
              />
            )}
          </form.AppField>

          <form.AppField name="versioning">
            {(field) => (
              <field.Switch
                label={t('Plans.Form.Versioning')}
                disabled={disabled}
              />
            )}
          </form.AppField>

          <Heading level={3} additionalClasses="dark:text-white">
            {t('Plans.Heading.Billing')}
          </Heading>

          <form.AppField name="creditCard">
            {(field) => (
              <field.Input
                label={t('Plans.Form.CreditCard')}
                disabled={disabled}
              />
            )}
          </form.AppField>

          <form.AppField name="monthlyAnnual">
            {(field) => (
              <field.Switch
                label={t('Plans.Form.MonthlyAnnual')}
                disabled={disabled}
              />
            )}
          </form.AppField>

          <form.AppField name="invoice">
            {(field) => (
              <field.Switch
                label={t('Plans.Form.Invoice')}
                disabled={disabled}
              />
            )}
          </form.AppField>

          <form.AppField name="usageDashboard">
            {(field) => (
              <field.Switch
                label={t('Plans.Form.UsageDashboard')}
                disabled={disabled}
              />
            )}
          </form.AppField>
        </div>

        <form.DirtyHandler />
        <form.ValidationToastHandler />
        <form.FormStateHandler setFormState={setFormState} />
      </form>
    </form.AppForm>
  );
};

export default PlanForm;
